import React, { useRef, useEffect, useState } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
  MotionValue,
} from 'framer-motion';

// =============================================================================
// SCROLL REVEAL (Animate on viewport entry)
// =============================================================================

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  delay?: number;
  duration?: number;
  once?: boolean;
  threshold?: number;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className,
  style,
  direction = 'up',
  delay = 0,
  duration = 0.5,
  once = true,
  threshold = 0.2,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once,
    amount: threshold,
  });

  const directionOffsets = {
    up: { y: 40 },
    down: { y: -40 },
    left: { x: 40 },
    right: { x: -40 },
    none: {},
  };

  const initialState = {
    opacity: 0,
    ...directionOffsets[direction],
  };

  const animateState = {
    opacity: 1,
    x: 0,
    y: 0,
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={initialState}
      animate={isInView ? animateState : initialState}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// PARALLAX SECTION
// =============================================================================

interface ParallaxSectionProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  speed?: number; // -1 to 1, negative = opposite direction
  direction?: 'vertical' | 'horizontal';
}

export const ParallaxSection: React.FC<ParallaxSectionProps> = ({
  children,
  className,
  style,
  speed = 0.5,
  direction = 'vertical',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const range = 100 * speed;
  const transform = useTransform(scrollYProgress, [0, 1], [-range, range]);
  const springTransform = useSpring(transform, { stiffness: 100, damping: 30 });

  return (
    <div
      ref={ref}
      className={className}
      style={{
        overflow: 'hidden',
        ...style,
      }}
    >
      <motion.div
        style={{
          [direction === 'vertical' ? 'y' : 'x']: springTransform,
        }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// =============================================================================
// SCROLL PROGRESS HOOK
// =============================================================================

export const useScrollProgress = (
  target?: React.RefObject<HTMLElement>
): {
  scrollProgress: MotionValue<number>;
  scrollY: MotionValue<number>;
  isAtTop: boolean;
  isAtBottom: boolean;
} => {
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const { scrollYProgress, scrollY } = useScroll({
    target: target?.current ? target : undefined,
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (value) => {
      setIsAtTop(value <= 0.01);
      setIsAtBottom(value >= 0.99);
    });

    return () => unsubscribe();
  }, [scrollYProgress]);

  return {
    scrollProgress: scrollYProgress,
    scrollY,
    isAtTop,
    isAtBottom,
  };
};

// =============================================================================
// SCROLL PROGRESS BAR
// =============================================================================

interface ScrollProgressBarProps {
  className?: string;
  style?: React.CSSProperties;
  color?: string;
  height?: number;
  position?: 'top' | 'bottom';
}

export const ScrollProgressBar: React.FC<ScrollProgressBarProps> = ({
  className,
  style,
  color = 'var(--accent, #3b82f6)',
  height = 3,
  position = 'top',
}) => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      className={className}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        [position]: 0,
        height,
        background: color,
        transformOrigin: 'left',
        zIndex: 9999,
        scaleX,
        ...style,
      }}
    />
  );
};

// =============================================================================
// STICKY SCROLL SECTION
// =============================================================================

interface StickyScrollSectionProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  stickyHeight?: string; // CSS height (e.g., '200vh')
}

export const StickyScrollSection: React.FC<StickyScrollSectionProps> = ({
  children,
  className,
  style,
  stickyHeight = '200vh',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height: stickyHeight,
        position: 'relative',
        ...style,
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {typeof children === 'function'
          ? (children as (progress: MotionValue<number>) => React.ReactNode)(
              scrollYProgress
            )
          : children}
      </div>
    </div>
  );
};

// =============================================================================
// SCROLL FADE
// =============================================================================

interface ScrollFadeProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  fadeDistance?: number;
}

export const ScrollFade: React.FC<ScrollFadeProps> = ({
  children,
  className,
  style,
  fadeDistance = 100,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', `start ${fadeDistance}px`],
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        opacity,
        ...style,
      }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// SCROLL SCALE
// =============================================================================

interface ScrollScaleProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  scaleRange?: [number, number];
}

export const ScrollScale: React.FC<ScrollScaleProps> = ({
  children,
  className,
  style,
  scaleRange = [0.8, 1],
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });

  const scale = useTransform(scrollYProgress, [0, 1], scaleRange);
  const springScale = useSpring(scale, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        scale: springScale,
        ...style,
      }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// SCROLL COUNTER (Number that counts as you scroll)
// =============================================================================

interface ScrollCounterProps {
  from?: number;
  to: number;
  className?: string;
  style?: React.CSSProperties;
  formatOptions?: Intl.NumberFormatOptions;
  prefix?: string;
  suffix?: string;
}

export const ScrollCounter: React.FC<ScrollCounterProps> = ({
  from = 0,
  to,
  className,
  style,
  formatOptions,
  prefix = '',
  suffix = '',
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end center'],
  });

  const value = useTransform(scrollYProgress, [0, 1], [from, to]);
  const springValue = useSpring(value, { stiffness: 100, damping: 30 });
  const [displayValue, setDisplayValue] = useState(from);

  useEffect(() => {
    const unsubscribe = springValue.on('change', (v) => {
      setDisplayValue(v);
    });
    return () => unsubscribe();
  }, [springValue]);

  const formatted = formatOptions
    ? new Intl.NumberFormat('en-US', formatOptions).format(displayValue)
    : Math.round(displayValue).toString();

  return (
    <span ref={ref} className={className} style={style}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

// =============================================================================
// INFINITE SCROLL TRIGGER
// =============================================================================

interface InfiniteScrollTriggerProps {
  onTrigger: () => void;
  threshold?: number;
  disabled?: boolean;
  className?: string;
}

export const InfiniteScrollTrigger: React.FC<InfiniteScrollTriggerProps> = ({
  onTrigger,
  threshold = 0,
  disabled = false,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: threshold });

  useEffect(() => {
    if (isInView && !disabled) {
      onTrigger();
    }
  }, [isInView, disabled, onTrigger]);

  return <div ref={ref} className={className} style={{ height: 1 }} />;
};

export default {
  ScrollReveal,
  ParallaxSection,
  useScrollProgress,
  ScrollProgressBar,
  StickyScrollSection,
  ScrollFade,
  ScrollScale,
  ScrollCounter,
  InfiniteScrollTrigger,
};
